<script setup>
import { useTemplateRef } from 'vue';
import {
  dataNotAvailable,
  formatPercent,
  formatSnow,
  getAlertColorForeground,
  isEstimatedSource,
  isMetric,
  isScreenLg,
  safeRound,
} from '@@/utils/CommonUtils';
import { canBeResponsive, chartColors } from '@@/utils/ChartUtils';
import { createChartDataSets, updateChartDataSets } from '@@/utils/PowderForecastAlertUtils';
import { useMetaStore } from '@@/stores/Meta';
import { useUserStore } from '@@/stores/User';

const props = defineProps({
  canShowChartHeader: {
    type: Boolean,
    default: true,
  },
  endDay: {
    type: Number,
    default: 4,
  },
  isCompare: {
    type: Boolean,
    default: true,
  },
  isSkeleton: {
    type: Boolean,
    default: false,
  },
  location: {
    type: Object,
    required: true,
  },
  mobileDateLongNoWrap: {
    type: Boolean,
    default: false,
  },
  mobileDateLongWrap: {
    type: Boolean,
    default: false,
  },
  mobileDateShort: {
    type: Boolean,
    default: true,
  },
  range: {
    type: Object,
    default: null,
  },
  startDay: {
    type: Number,
    default: 0,
  },
  title: {
    type: String,
    default: '',
  },
  useHistory: {
    type: Boolean,
    default: false,
  },
});

const { $chart, $dayjs } = useNuxtApp();
const metaStore = useMetaStore();
const userStore = useUserStore();
const ctx = useTemplateRef('ctx');

const isAllAccess = computed(() => userStore.isAllAccess);
const theme = computed(() => userStore.computedTheme);
const units = computed(() => userStore.preferences.units);

const forecasts = computed(() => {
  const field = props.useHistory ? 'history_snow_daily' : 'forecast_snow_daily';

  if (props.location?.[field]?.length) {
    return props.location[field].map((day) => ({
      ...day,
      precip_snow: safeRound(day.precip_snow),
    }));
  }

  return [];
});

/**
 * When using OpenMountain-API return the precip_snow value from the appropriate forecast
 * snow summary index, either days 1-5 or days 6-10, 0 or 1, respectively.
 */
const snowSummary = computed(() => {
  const field = props.useHistory ? 'history_snow_summary' : 'forecast_snow_summary';
  const index = props.startDay === 0 && props.endDay === 4 ? 0 : 1;
  return props.location?.[field]?.[index];
});

const amountCumulative = computed(() => {
  // Return undefined if there are no forecast days.
  if (forecasts.value.length === 0) {
    return undefined;
  }

  return snowSummary.value?.precip_snow;
});

const amountCumulativeFormatted = computed(() => {
  if (isMetric(units.value)) {
    return safeRound(amountCumulative.value);
  }

  return formatSnow(safeRound(amountCumulative.value), units.value, true);
});

/**
 * Historic cumulative snowfall amounts can only be shown to All-Access users. Forecast
 * cumulative snowfall amounts can be shown to all users.
 */
const canShowAmountCumulative = computed(() => props.useHistory ? isAllAccess.value : true);

const chartContainerClass = computed(() => {
  return [
    props.useHistory && props.isCompare ? 'tw-h-20' : null,
    'tw-w-full tw-relative',
  ];
});

const forecastTitle = computed(() => props.title || `Next ${props.startDay + 1}-${props.endDay + 1} Days`);

const labels = computed(() => {
  const numDays = props.endDay - props.startDay + 1;

  return forecasts.value
    .slice(props.startDay, props.endDay + 1)
    .map((day) => {
      const dayOfWeekShort = $dayjs(day.display_at)
        .format('dd')
        .slice(0, 1);
      const [dayOfWeek, dayOfMonth] = day.display_at_local_label.split(' ');

      return {
        className: props.useHistory && isEstimatedSource(day.source_id, metaStore.dataSourcesById)
          ? 'estimated-border'
          : null,
        desktop: day.display_at_local_label,
        mobile: { dayOfWeek, dayOfWeekShort, dayOfMonth },
        width: formatPercent(1 / numDays),
      };
    });
});

const listClass = computed(() => ([
  'tw-w-full',
  props.isCompare && !props.useHistory && !props.isSkeleton ? 'tw-h-4' : null,
  'lg:tw-h-6',
  'tw-list-none',
]));

const subHeaderTextStyle = computed(() => {
  let color = 'var(--text-darkest)';

  if (canShowAmountCumulative.value) {
    const powderAlert = metaStore.getPowderAlert(snowSummary.value?.alerts);
    color = getAlertColorForeground(powderAlert);
  }
  else {
    color = 'var(--saturated-teal)';
  }

  return { color };
});

const getConfig = () => {
  const getPaddingTop = () => {
    if (isScreenLg()) {
      return 22;
    }

    if (props.useHistory) {
      return 16;
    }

    if (props.isCompare) {
      return 0;
    }

    return 16;
  };

  const isSkeleton = () => props.isSkeleton;

  const config = {
    data: {
      datasets: [],
      labels: forecasts.value.slice(props.startDay, props.endDay + 1).map(() => ''),
    },
    options: {
      datasets: {
        bar: {
          barPercentage: 0.85,
          categoryPercentage: 1,
        },
      },
      layout: {
        padding: {
          bottom: -20,
          left: -20,
          top: getPaddingTop(),
        },
      },
      maintainAspectRatio: false,
      plugins: {
        datalabels: {
          align: 'end',
          anchor: 'end',
          font: {
            weight: '500',
            size: isScreenLg() ? 16 : 11,
          },
          formatter: (value, context) => {
            if (isSkeleton()) {
              return '';
            }

            if (value === undefined) {
              const { chart, dataIndex } = context;
              const hasNoData = chart.data.datasets
                .every((ds) => ds.data?.[dataIndex] === undefined);

              return isScreenLg() && hasNoData ? dataNotAvailable : '';
            }

            if (props.useHistory && isScreenLg()) {
              // Show precip snow amounts with units in history view on large screens
              return formatSnow(value, units.value, true);
            }

            if (props.useHistory && !isScreenLg()) {
              // Show precip snow amounts without units in history view on smaller screens
              return value;
            }

            if (!isScreenLg() && value === 0) {
              // Don't display 0 precip snow amounts on mobile
              return '';
            }

            if (!isScreenLg() && props.isCompare) {
              // Don't display precip snow amounts on mobile devices in compare views
              return '';
            }

            // Otherwise display precip snow amounts without units
            return value;
          },
          offset: 0,
          padding: {
            bottom: isScreenLg() ? 2 : 0,
          },
          textAlign: 'center',
        },
        legend: {
          display: false,
        },
        title: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      responsive: canBeResponsive(),
      scales: {},
    },
    type: 'bar',
  };

  return config;
};

const getDataSets = () => {
  const dataSets = createChartDataSets({ includeNotAvailable: true, theme });

  for (let day = props.startDay; day <= props.endDay; day += 1) {
    const forecast = forecasts.value.length && day < forecasts.value.length
      ? forecasts.value[day]
      : {};

    updateChartDataSets({
      dataSets,
      forecastPeriod: forecast,
      snowValue: forecast.precip_snow,
    });
  }

  if (props.isSkeleton) {
    Object.values(dataSets)
      .forEach((dataSet) => dataSet.backgroundColor = chartColors.skeletonColor(theme.value));
  }

  return Object.values(dataSets);
};

const getDateClassDesktop = (labelClassName) => `tw-hidden lg:tw-inline-block tw-pb-px tw-font-normal tw-text-xs ${labelClassName}`;

const getDateClassMobile = (labelClassName) => ([
  'tw-inline-block lg:tw-hidden',
  props.useHistory ? 'tw-pb-px' : `${props.isSkeleton ? '' : 'tw-px-1'} tw-align-top`,
  'tw-font-normal tw-text-xs',
  props.isSkeleton ? 'tw-mt-1 lg:tw-mt-2' : '',
  labelClassName,
]);

const getScales = () => {
  // Default to 9 if no snow is forecast in the next 10 days
  let maxSnowForecastDaily = Math.max(...forecasts.value.map((day) => day.precip_snow))
    || 9;

  // Use either the maximum range when present or the max daily snow forecast
  maxSnowForecastDaily = props.range?.max || maxSnowForecastDaily;

  return {
    x: {
      border: {
        display: false,
      },
      grid: {
        display: false,
      },
      stacked: true,
      ticks: {
        color: chartColors.textRegular(theme.value),
        font: {
          weight: '500',
          size: 12,
        },
        padding: -2,
      },
    },
    y: {
      beginAtZero: false,
      border: {
        display: false,
      },
      grid: {
        display: false,
      },
      max: maxSnowForecastDaily,
      min: 0,
      stacked: true,
      ticks: {
        display: false,
      },
    },
  };
};

let barChart;

const renderChart = () => {
  if (barChart) {
    // After destroying the chart re-render it in a timeout.
    // SEE: https://stackoverflow.com/questions/40056555/destroy-chart-js-bar-graph-to-redraw-other-graph-in-same-canvas
    barChart.destroy();
    barChart = null;
    window.setTimeout(() => renderChart());
    return;
  }

  const config = getConfig();

  config.data.datasets = getDataSets();
  config.options.scales = getScales();

  barChart = new $chart(ctx.value, config);
};

onMounted(() => {
  if (isAllAccess.value) {
    renderChart();
    watch(() => props.location, renderChart);
    watch(theme, renderChart);
  }
});
</script>

<template>
  <div>
    <SnowChartHeader
      v-if="canShowChartHeader"
      :amount-cumulative="amountCumulativeFormatted"
      :amount-cumulative-text-style="subHeaderTextStyle"
      :can-show-amount-cumulative="canShowAmountCumulative"
      :is-skeleton="isSkeleton"
      :label="forecastTitle"
    />
    <div
      v-if="isAllAccess || isSkeleton"
      :class="chartContainerClass"
    >
      <div
        ref="ctxContainer"
        class="tw-h-9 lg:tw-h-12"
      >
        <canvas ref="ctx" />
      </div>
      <ul :class="listClass">
        <li
          v-for="label in labels"
          :key="label.long"
          class="tw-inline-block tw-h-full tw-text-center"
          :style="{ width: label.width }"
        >
          <div
            v-if="mobileDateLongWrap"
            :class="getDateClassMobile(label.className)"
          >
            <span
              :class="[
                'tw-block',
                isSkeleton ? 'skeleton-copy' : 'text-dark-color',
              ]"
            >
              {{ label.mobile.dayOfWeek }}
            </span>
            <span
              :class="[
                'tw-block tw--mt-px tw-font-medium',
                isSkeleton ? 'skeleton-copy' : '',
              ]"
            >
              {{ label.mobile.dayOfMonth }}
            </span>
          </div>
          <div
            v-else-if="mobileDateLongNoWrap"
            :class="getDateClassMobile(label.className)"
          >
            <span :class="isSkeleton ? 'skeleton-copy' : 'text-dark-color'">
              {{ label.mobile.dayOfWeek }}
            </span>
            <span :class="isSkeleton ? 'skeleton-copy' : 'tw-font-medium'">
              {{ label.mobile.dayOfMonth }}
            </span>
          </div>
          <div
            v-else-if="mobileDateShort"
            :class="getDateClassMobile(label.className)"
          >
            <span
              :class="[
                'tw-px-1',
                isSkeleton ? 'skeleton-copy' : '',
              ]"
            >
              {{ label.mobile.dayOfWeekShort }}
            </span>
          </div>
          <div
            :class="getDateClassDesktop(label.className)"
          >
            <span :class="isSkeleton ? 'skeleton-copy' : ''">
              {{ label.desktop }}
            </span>
          </div>
        </li>
      </ul>
    </div>
    <div
      v-else
      class="tw-h-18 tw-w-full"
    >
      <SnowForecastLockedChart
        :end-day="endDay"
        :location="location"
        :start-day="startDay"
        :use-history="useHistory"
      />
    </div>
  </div>
</template>
